// src/views/bots/hooks/useQuestionnaireManagement.ts
// 🧠 QUESTIONNAIRE MANAGEMENT LOGIC - Enhanced with Interactive Questions
// ✅ FIXED: Added missing return statement - this was causing void return!

import { useCallback, useMemo } from 'react';
import { QuestionItem } from './useBotForm';
import logger from '../../../utils/logger';

interface UseQuestionnaireProps {
  questions: QuestionItem[];
  onQuestionsChange: (questions: QuestionItem[]) => void;
}

// 🧠 ENHANCED QUESTIONNAIRE MANAGEMENT HOOK - Interactive Support
export const useQuestionnaireManagement = ({ questions, onQuestionsChange }: UseQuestionnaireProps) => {
  logger.log('🧠 useQuestionnaireManagement: Hook initialized with', questions.length, 'questions');
  
  // 🔥 ENHANCED ADD QUESTION: Support for specific question types
 const addQuestion = useCallback((questionType: QuestionItem['type'] = 'text') => {
  logger.log('➕ useQuestionnaire: Adding new question of type:', questionType);
  
  const baseQuestion: QuestionItem = {
    id: `q_${Date.now()}`,
    question: '',
    type: questionType,
    required: true,
    order: questions.length
  };

  // 🔥 SET TYPE-SPECIFIC DEFAULTS
  if (questionType === 'interactive-list') {
    baseQuestion.question = 'Please select an option from the list';
    baseQuestion.interactive = {
      type: 'list' as const,
      header: { type: 'text' as const, text: 'Choose an option' },
      body: { text: 'Please select one of the options below' },
      action: {
        button: 'View Options',
        sections: [
          {
            title: 'Options',
            rows: [
              { id: `option_${Date.now()}_1`, title: 'Option 1', description: 'First option' },
              { id: `option_${Date.now()}_2`, title: 'Option 2', description: 'Second option' }
            ]
          }
        ]
      }
    };
  } else if (questionType === 'interactive-button') {
    baseQuestion.question = 'Please choose one of the options';
    baseQuestion.interactive = {
      type: 'button' as const,
      header: { type: 'text' as const, text: 'Choose an option' },
      body: { text: 'Please select one of the options below' },
      action: {
        buttons: [
          { type: 'reply', reply: { id: `btn_${Date.now()}_1`, title: 'Yes' } },
          { type: 'reply', reply: { id: `btn_${Date.now()}_2`, title: 'No' } }
        ]
      }
    };
  } else if (questionType === 'location') {
    baseQuestion.question = 'Please share your location';
  } else if (questionType === 'template') {
    // 🔥 TEMPLATE QUESTIONS: Empty question text and initialize template data
    baseQuestion.question = '';
    (baseQuestion as any).templateData = {
      templateId: '',
      templateName: '',
      template: null
    };
  } else {
    // 🔥 FIXED: Complete default questions object with all types
    const defaultQuestions: Record<string, string> = {
      'text': 'Please enter your answer',
      'email': 'Please enter your email address',
      'phone': 'Please enter your phone number',
      'number': 'Please enter a number',
      'interactive-list': 'Please select an option from the list',
      'interactive-button': 'Please choose one of the options',
      'location': 'Please share your location',
      'template': ''
    };
    baseQuestion.question = defaultQuestions[questionType] || 'Please answer this question';
  }
  
  logger.log('✅ useQuestionnaire: Created question with defaults:', baseQuestion);
  onQuestionsChange([...questions, baseQuestion]);
}, [questions, onQuestionsChange]);

  // 🔥 ENHANCED UPDATE QUESTION: Fixed to properly handle all field updates
  const updateQuestion = useCallback((questionId: string, field: keyof QuestionItem, value: any) => {
    logger.log('📝 useQuestionnaire: Updating question:', { questionId, field, value });
    
    // 🔥 FIND QUESTION: Ensure question exists before updating
    const questionExists = questions.find(q => q.id === questionId);
    if (!questionExists) {
      console.error('❌ useQuestionnaire: Question not found:', questionId);
      return;
    }
    
    // 🔥 SPECIAL HANDLING: Interactive field updates
    if (field === 'interactive') {
      logger.log('🎯 useQuestionnaire: Updating interactive data:', value);
    }
    
    // 🔥 UPDATE QUESTIONS: Map through and update the specific question
    const updatedQuestions = questions.map(q => {
      if (q.id === questionId) {
        const updatedQuestion = { ...q, [field]: value };
        
        // 🔥 CLEAN UP: Remove interactive data if changing away from interactive types
        if (field === 'type' && !['interactive-list', 'interactive-button'].includes(value as string)) {
          delete (updatedQuestion as any).interactive;
          logger.log('🧹 useQuestionnaire: Cleaned interactive data for non-interactive type');
        }
        
        // 🔥 INITIALIZE: Add default interactive data if changing to interactive type
        if (field === 'type') {
          if (value === 'interactive-list') {
            updatedQuestion.interactive = {
              type: 'list' as const,
              header: { type: 'text' as const, text: 'Choose an option' },
              body: { text: updatedQuestion.question || 'Please select from the list' },
              action: {
                button: 'View Options',
                sections: [
                  {
                    title: 'Options',
                    rows: [
                      { id: `option_${Date.now()}_1`, title: 'Option 1', description: '' }
                    ]
                  }
                ]
              }
            };
            logger.log('🎯 useQuestionnaire: Initialized list interactive data');
          } else if (value === 'interactive-button') {
            updatedQuestion.interactive = {
              type: 'button' as const,
              header: { type: 'text' as const, text: 'Choose an option' },
              body: { text: updatedQuestion.question || 'Please select an option' },
              action: {
                buttons: [
                  { type: 'reply', reply: { id: `btn_${Date.now()}_1`, title: 'Yes' } },
                  { type: 'reply', reply: { id: `btn_${Date.now()}_2`, title: 'No' } }
                ]
              }
            };
            logger.log('🎯 useQuestionnaire: Initialized button interactive data');
          }
        }
        
        logger.log('✅ useQuestionnaire: Question updated successfully:', updatedQuestion.id);
        return updatedQuestion;
      }
      return q;
    });
    
    logger.log('📊 useQuestionnaire: Updated questions count:', updatedQuestions.length);
    onQuestionsChange(updatedQuestions);
  }, [questions, onQuestionsChange]);

  // 🔥 REMOVE QUESTION: Delete question by ID
  const removeQuestion = useCallback((questionId: string) => {
    logger.log('🗑️ useQuestionnaire: Removing question:', questionId);
    
    const filteredQuestions = questions.filter(q => q.id !== questionId);
    
    // 🎯 UPDATE ORDER: Reorder remaining questions
    const reorderedQuestions = filteredQuestions.map((q, index) => ({ 
      ...q, 
      order: index 
    }));
    
    logger.log('✅ useQuestionnaire: Question removed, reordered', reorderedQuestions.length, 'remaining questions');
    onQuestionsChange(reorderedQuestions);
  }, [questions, onQuestionsChange]);

  // 🔥 MOVE QUESTION: Reorder questions (up/down)
  const moveQuestion = useCallback((fromIndex: number, direction: number) => {
    const toIndex = fromIndex + direction;
    
    // 🎯 BOUNDS CHECK: Ensure valid movement
    if (toIndex < 0 || toIndex >= questions.length) {
      logger.log('❌ useQuestionnaire: Invalid move operation:', { fromIndex, toIndex, direction });
      return;
    }
    
    logger.log('🔄 useQuestionnaire: Moving question:', { fromIndex, toIndex, direction });
    
    const newQuestions = [...questions];
    
    // 🎯 SWAP POSITIONS: Exchange two questions
    [newQuestions[fromIndex], newQuestions[toIndex]] = [newQuestions[toIndex], newQuestions[fromIndex]];
    
    // 🎯 UPDATE ORDER: Fix order numbers after swap
    const reorderedQuestions = newQuestions.map((q, index) => ({ 
      ...q, 
      order: index 
    }));
    
    logger.log('✅ useQuestionnaire: Questions reordered successfully');
    onQuestionsChange(reorderedQuestions);
  }, [questions, onQuestionsChange]);

  // 🔥 ENHANCED QUESTION VALIDATION: Supports interactive questions
  const validateQuestions = useCallback(() => {
    logger.log('🔍 useQuestionnaire: Validating questions...');
    const errors: Record<string, string> = {};
    
    if (questions.length === 0) {
      errors.questions = 'At least one question is required for questionnaire mode';
      return { isValid: false, errors };
    }
    
    // 🎯 CHECK EACH QUESTION: Ensure all have text
    const invalidQuestions = questions.filter(q => !q.question.trim());
    if (invalidQuestions.length > 0) {
      errors.questions = 'All questions must have text';
      return { isValid: false, errors };
    }
    
    // 🔥 INTERACTIVE: Validate interactive questions with proper null checking
    const invalidInteractiveQuestions = questions.filter(q => {
      if (q.type === 'interactive-list' && q.interactive) {
        const hasValidSections = q.interactive.action && 
          q.interactive.action.sections && 
          q.interactive.action.sections.length > 0 &&
          q.interactive.action.sections.every((section: any) => 
            section.rows && section.rows.length > 0 && section.rows.every((row: any) => row.title && row.title.trim())
          );
        if (!hasValidSections) {
          logger.log('❌ useQuestionnaire: Invalid interactive list question:', q.id);
          return true;
        }
      }
      
      if (q.type === 'interactive-button' && q.interactive) {
        const hasValidButtons = q.interactive.action && 
          q.interactive.action.buttons && 
          q.interactive.action.buttons.length > 0 && 
          q.interactive.action.buttons.every((btn: any) => btn.reply && btn.reply.title && btn.reply.title.trim());
        if (!hasValidButtons) {
          logger.log('❌ useQuestionnaire: Invalid interactive button question:', q.id);
          return true;
        }
      }
      
      return false;
    });
    
    if (invalidInteractiveQuestions.length > 0) {
      errors.questions = 'Interactive questions must have valid options configured';
      return { isValid: false, errors };
    }
    
    logger.log('✅ useQuestionnaire: All questions validated successfully');
    return { isValid: true, errors: {} };
  }, [questions]);

  // 🔥 QUESTION UTILITIES: Helper functions
  const getQuestionByIndex = useCallback((index: number): QuestionItem | undefined => {
    return questions[index];
  }, [questions]);

  const getQuestionById = useCallback((questionId: string): QuestionItem | undefined => {
    return questions.find(q => q.id === questionId);
  }, [questions]);

  const canMoveUp = useCallback((index: number): boolean => {
    return index > 0;
  }, []);

  const canMoveDown = useCallback((index: number): boolean => {
    return index < questions.length - 1;
  }, [questions.length]);

  // 🔥 ENHANCED QUESTION STATS: Includes interactive question types
  const questionStats = useMemo(() => {
    const stats = {
      total: questions.length,
      required: questions.filter(q => q.required).length,
      optional: questions.filter(q => !q.required).length,
      byType: {
        // 🎯 EXISTING TYPES: Preserved
        text: questions.filter(q => q.type === 'text').length,
        email: questions.filter(q => q.type === 'email').length,
        phone: questions.filter(q => q.type === 'phone').length,
        number: questions.filter(q => q.type === 'number').length,
        // 🔥 NEW TYPES: Interactive question tracking
        'interactive-list': questions.filter(q => q.type === 'interactive-list').length,
        'interactive-button': questions.filter(q => q.type === 'interactive-button').length,
        location: questions.filter(q => q.type === 'location').length,
        // 🔥 TEMPLATE: Template question tracking
        template: questions.filter(q => q.type === 'template').length,
      }
    };
    
    logger.log('📊 useQuestionnaire: Question stats computed:', stats);
    return stats;
  }, [questions]);

  // 🔥 ENHANCED TYPE DETECTION: Utility to detect interactive questions
  const hasInteractiveQuestions = useMemo(() => {
    const interactiveCount = questionStats.byType['interactive-list'] + 
                           questionStats.byType['interactive-button'] + 
                           questionStats.byType.location;
    return interactiveCount > 0;
  }, [questionStats]);

  // 🔥 QUESTION TYPE SUMMARY: For debugging and analytics
  const getQuestionTypeSummary = useCallback(() => {
    const summary = {
      basicQuestions: questionStats.byType.text + questionStats.byType.email + 
                     questionStats.byType.phone + questionStats.byType.number,
      interactiveQuestions: questionStats.byType['interactive-list'] + 
                           questionStats.byType['interactive-button'],
      specialQuestions: questionStats.byType.location,
      totalComplexity: questions.reduce((complexity, q) => {
        // 🎯 COMPLEXITY SCORING: Different question types have different complexity
        switch (q.type) {
          case 'text': return complexity + 1;
          case 'email': return complexity + 1;
          case 'phone': return complexity + 1;
          case 'number': return complexity + 1;
          case 'interactive-list': 
            const listComplexity = (q.interactive && q.interactive.action && q.interactive.action.sections) 
              ? q.interactive.action.sections.reduce((acc: number, section: any) => 
                  acc + (section.rows ? section.rows.length : 0), 0) 
              : 1;
            return complexity + listComplexity + 2; // Base complexity + options
          case 'interactive-button':
            const buttonComplexity = (q.interactive && q.interactive.action && q.interactive.action.buttons) 
              ? q.interactive.action.buttons.length 
              : 1;
            return complexity + buttonComplexity + 1; // Base complexity + buttons
          case 'location': return complexity + 2; // Location is moderately complex
          default: return complexity + 1;
        }
      }, 0)
    };
    
    logger.log('🎯 useQuestionnaire: Question summary:', summary);
    return summary;
  }, [questions, questionStats]);

  // 🔥 THE MISSING RETURN STATEMENT - This was the bug!
  logger.log('✅ useQuestionnaireManagement: Returning hook interface with all methods');
  
  return {
    // ✅ Core data
    questions,
    
    // ✅ Core actions
    addQuestion,
    updateQuestion,
    removeQuestion,
    moveQuestion,
    
    // ✅ Utilities
    canMoveUp,
    canMoveDown,
    getQuestionByIndex,
    getQuestionById,
    
    // ✅ Validation
    validateQuestions,
    
    // ✅ Stats and analytics
    questionStats,
    hasInteractiveQuestions,
    getQuestionTypeSummary
  };
};